import api from '../config/api';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';

export const profilesKeys = {
  list: (params) => ['profiles', 'list', params || {}],
  detail: (id) => ['profiles', 'detail', String(id || '')],
  byUsername: (username) => ['profiles', 'byUsername', String(username || '')],
  bySlug: (slug) => ['profiles', 'bySlug', String(slug || '')],
  gallery: (id) => ['profiles', 'gallery', String(id || '')],
  videos: (id) => ['profiles', 'videos', String(id || '')],
  audio: (id) => ['profiles', 'audio', String(id || '')]
};

export function useProfilesList(params) {
  return useQuery(
    profilesKeys.list(params),
    async () => {
      const res = await api.get('/api/profiles', { params });
      return res.data;
    },
    { keepPreviousData: true }
  );
}

/**
 * Infinite-scrolling profiles list for /feed.
 * Backend already supports page/limit/q.
 */
export function useInfiniteProfilesList(params) {
  const safe = params || {};
  const limit = Number(safe.limit) || 24;

  return useInfiniteQuery(
    ['profiles', 'infinite', safe],
    async ({ pageParam = 1 }) => {
      const res = await api.get('/api/profiles', {
        params: { ...safe, page: pageParam, limit }
      });
      return res.data;
    },
    {
      getNextPageParam: (lastPage, pages) => {
        const p = lastPage?.pagination ?? {};
        const page = Number(p.page) || pages.length || 1;
        const total = Number(p.total);
        const pageLimit = Number(p.limit) || limit;
        if (!Number.isNaN(total) && total > 0) {
          if (page * pageLimit >= total) return undefined;
          return page + 1;
        }
        // fallback: stop when last page returned fewer than limit
        const count = Array.isArray(lastPage?.profiles) ? lastPage.profiles.length : 0;
        if (count < pageLimit) return undefined;
        return page + 1;
      },
      keepPreviousData: true
    }
  );
}

export function useProfile(id) {
  return useQuery(
    profilesKeys.detail(id),
    async () => {
      const res = await api.get(`/api/profiles/${encodeURIComponent(id)}`);
      return res.data;
    },
    { enabled: !!id }
  );
}

export function useProfileByUsername(username) {
  return useQuery(
    profilesKeys.byUsername(username),
    async () => {
      const res = await api.get(`/api/profiles/by-username/${encodeURIComponent(username)}`);
      return res.data;
    },
    { enabled: !!username }
  );
}

/**
 * Canonical profile resolver (preferred): slug-based lookup.
 * Backend endpoint is still named by-username for backward compatibility,
 * but it now accepts either username_slug or legacy username.
 */
export function useProfileBySlug(slug) {
  return useQuery(
    profilesKeys.bySlug(slug),
    async () => {
      const res = await api.get(`/api/profiles/by-username/${encodeURIComponent(slug)}`);
      return res.data;
    },
    { enabled: !!slug }
  );
}

export function useUpdateProfileCategory(id) {
  const qc = useQueryClient();
  return useMutation(
    async (payload) => {
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/category`, payload);
      return res.data;
    },
    {
      onSuccess: (data) => {
        // If the server returns pendingApproval=true we intentionally do NOT refetch,
        // otherwise the UI would snap back to the old category path.
        if (data?.pendingApproval) return;
        qc.invalidateQueries(profilesKeys.detail(id));
        qc.invalidateQueries(['profiles']);
      }
    }
  );
}

export function useUpdateProfileTags(id) {
  const qc = useQueryClient();
  return useMutation(
    async (tags) => {
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/tags`, { tags });
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.detail(id));
        qc.invalidateQueries(['profiles']);
      }
    }
  );
}

// =====================
// Gallery
// =====================

export function useProfileGallery(id, opts) {
  return useQuery(
    profilesKeys.gallery(id),
    async () => {
      const res = await api.get(`/api/profiles/${encodeURIComponent(id)}/gallery`);
      return res.data;
    },
    { enabled: !!id, ...(opts || {}) }
  );
}

export function useUploadProfileGalleryImage(id) {
  const qc = useQueryClient();
  return useMutation(
    async (file) => {
      const form = new FormData();
      form.append('image', file);
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/gallery`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.gallery(id));
        qc.invalidateQueries(profilesKeys.detail(id));
        qc.invalidateQueries(['profiles', 'byUsername']); // conservative
      }
    }
  );
}

export function useDeleteProfileGalleryImage(id) {
  const qc = useQueryClient();
  return useMutation(
    async (imageId) => {
      const res = await api.delete(`/api/profiles/${encodeURIComponent(id)}/gallery/${encodeURIComponent(imageId)}`);
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.gallery(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

export function useReorderProfileGallery(id) {
  const qc = useQueryClient();
  return useMutation(
    async (orderedIds) => {
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/gallery/reorder`, { orderedIds });
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.gallery(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

// =====================
// Video Embeds
// =====================

export function useProfileVideos(id, opts) {
  return useQuery(
    profilesKeys.videos(id),
    async () => {
      const res = await api.get(`/api/profiles/${encodeURIComponent(id)}/videos`);
      return res.data;
    },
    { enabled: !!id, ...(opts || {}) }
  );
}

export function useAddProfileVideo(id) {
  const qc = useQueryClient();
  return useMutation(
    async (payload) => {
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/videos`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.videos(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

export function useUpdateProfileVideo(id) {
  const qc = useQueryClient();
  return useMutation(
    async ({ videoId, ...payload }) => {
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/videos/${encodeURIComponent(videoId)}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.videos(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

export function useDeleteProfileVideo(id) {
  const qc = useQueryClient();
  return useMutation(
    async (videoId) => {
      const res = await api.delete(`/api/profiles/${encodeURIComponent(id)}/videos/${encodeURIComponent(videoId)}`);
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.videos(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

export function useReorderProfileVideos(id) {
  const qc = useQueryClient();
  return useMutation(
    async (orderedIds) => {
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/videos/reorder`, { orderedIds });
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.videos(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

// =====================
// Audio tracks
// =====================

export function useProfileAudio(id, opts) {
  return useQuery(
    profilesKeys.audio(id),
    async () => {
      const res = await api.get(`/api/profiles/${encodeURIComponent(id)}/audio`);
      return res.data;
    },
    { enabled: !!id, ...(opts || {}) }
  );
}

export function useUploadProfileAudio(id) {
  const qc = useQueryClient();
  return useMutation(
    async (file) => {
      const form = new FormData();
      form.append('audio', file);
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/audio`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.audio(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

export function useDeleteProfileAudio(id) {
  const qc = useQueryClient();
  return useMutation(
    async (trackId) => {
      const res = await api.delete(`/api/profiles/${encodeURIComponent(id)}/audio/${encodeURIComponent(trackId)}`);
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.audio(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}

export function useReorderProfileAudio(id) {
  const qc = useQueryClient();
  return useMutation(
    async (orderedIds) => {
      const res = await api.put(`/api/profiles/${encodeURIComponent(id)}/audio/reorder`, { orderedIds });
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(profilesKeys.audio(id));
        qc.invalidateQueries(profilesKeys.detail(id));
      }
    }
  );
}
